// 📄 components/ia/MarkdownNexus.tsx
// Markdown de las respuestas de Nexus (N2, 2026-10-27): antes se mostraban
// los **asteriscos** tal cual. Subconjunto chico y seguro, sin innerHTML:
// párrafos, títulos (#), listas (- / 1.), **negrita**, *cursiva*, `código`,
// bloques ``` y enlaces. Un enlace a una ruta interna (/proyecto/...) se
// muestra como botón que navega dentro de la app; uno https abre aparte.
import React from 'react';
import { ArrowRight } from 'lucide-react';

type Navegar = (ruta: string) => void;

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*)/g;

function inline(texto: string, navegar: Navegar, key: string): React.ReactNode[] {
  return texto.split(INLINE).filter(Boolean).map((parte, i) => {
    const k = `${key}-${i}`;
    if (parte.startsWith('**') && parte.endsWith('**')) return <strong key={k} className="font-semibold text-white">{parte.slice(2, -2)}</strong>;
    if (parte.startsWith('`') && parte.endsWith('`')) return <code key={k} className="px-1 py-0.5 rounded bg-black/30 text-[12px] text-blue-200">{parte.slice(1, -1)}</code>;
    const enlace = parte.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (enlace) {
      const [, label, url] = enlace;
      if (url.startsWith('/')) {
        return (
          <button
            key={k}
            onClick={() => navegar(url)}
            className="inline-flex items-center gap-1 mx-0.5 px-2 py-0.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-200 text-[12px] font-semibold hover:bg-blue-600/30 transition-colors align-baseline"
          >
            {label} <ArrowRight size={11} />
          </button>
        );
      }
      if (/^https:\/\//i.test(url)) {
        return <a key={k} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-300 underline">{label}</a>;
      }
      return <span key={k}>{label}</span>;
    }
    if (parte.length > 2 && parte.startsWith('*') && parte.endsWith('*')) return <em key={k}>{parte.slice(1, -1)}</em>;
    return <React.Fragment key={k}>{parte}</React.Fragment>;
  });
}

export default function MarkdownNexus({ texto, navegar }: { texto: string; navegar: Navegar }) {
  const lineas = texto.replace(/\r/g, '').split('\n');
  const bloques: React.ReactNode[] = [];
  let i = 0;

  while (i < lineas.length) {
    const linea = lineas[i];

    if (linea.trim().startsWith('```')) {
      const codigo: string[] = [];
      i++;
      while (i < lineas.length && !lineas[i].trim().startsWith('```')) codigo.push(lineas[i++]);
      i++;
      bloques.push(
        <pre key={`b${i}`} className="bg-black/40 border border-white/10 rounded-xl p-3 text-[12px] text-gray-200 overflow-x-auto whitespace-pre">{codigo.join('\n')}</pre>
      );
      continue;
    }

    const titulo = linea.match(/^#{1,4}\s+(.*)$/);
    if (titulo) {
      bloques.push(<p key={`b${i}`} className="font-bold text-white mt-1">{inline(titulo[1], navegar, `t${i}`)}</p>);
      i++;
      continue;
    }

    if (/^\s*([-*•]|\d+[.)])\s+/.test(linea)) {
      const ordenada = /^\s*\d+[.)]\s+/.test(linea);
      const items: string[] = [];
      while (i < lineas.length && /^\s*([-*•]|\d+[.)])\s+/.test(lineas[i])) {
        items.push(lineas[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i++;
      }
      const Lista = ordenada ? 'ol' : 'ul';
      bloques.push(
        <Lista key={`b${i}`} className={`${ordenada ? 'list-decimal' : 'list-disc'} pl-5 space-y-1`}>
          {items.map((it, j) => <li key={j}>{inline(it, navegar, `l${bloques.length}-${j}`)}</li>)}
        </Lista>
      );
      continue;
    }

    if (!linea.trim()) { i++; continue; }

    const parrafo: string[] = [];
    while (i < lineas.length && lineas[i].trim() && !/^\s*([-*•]|\d+[.)])\s+/.test(lineas[i]) && !/^#{1,4}\s/.test(lineas[i]) && !lineas[i].trim().startsWith('```')) {
      parrafo.push(lineas[i++]);
    }
    bloques.push(
      <p key={`b${i}`}>
        {parrafo.map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(l, navegar, `p${bloques.length}-${j}`)}</React.Fragment>)}
      </p>
    );
  }

  return <div className="text-sm leading-relaxed space-y-2">{bloques}</div>;
}
